import { useQuestionBuilder } from 'lib-node-question-builder';

import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import Box from '~/common/mui/Layout/Box';
import Loan1003FormContainer from '~/pages/Loan1003DataGrids/common/components/Loan1003FormContainer';
import Loan1003FormSection from '~/pages/Loan1003DataGrids/common/components/Loan1003FormSection';

import { useBorrower1003Store } from '../../../borrower1003Store';
import type { BorrowerDropdownOptions } from '../../../types';
import { AlsoKnownAsFieldArray } from './AlsoKnownAsFieldArray';
import { BorrowerInformationGrid } from './BorrowerInformationGrid';
import { PowerOfAttorney } from './PowerOfAttorney';
import { useBorrowerDataFormData, useBorrowerDataMutation } from './hooks';
import {
  dateOfBirthListener,
  isJoinedBorrowerChangedListener,
  isNameOrSsnChangedListener,
} from './listeners';

type BorrowerDataFormProps = {
  borrowerId: number;
};

const EMPTY_DROPDOWN_OPTIONS: BorrowerDropdownOptions = {
  borrowerPosition: [],
  maritalStatus: [],
  legalSpouse: [],
  ownershipStatus: [],
  citizenship: [],
  ownershipPropertyType: [],
  titleType: [],
  duExplanationForeclosure: [],
  duExplanationDelinquency: [],
  duExplanationBankruptcy: [],
  mannerPowerOfAttorneyHeld: [],
  joinedBorrowers: [],
};

export function BorrowerDataForm(props: Readonly<BorrowerDataFormProps>) {
  const isWriteAccessDisabled = useIsWriteAccessDisabled();

  const { data, isLoading } = useBorrowerDataFormData(props.borrowerId);

  const { mutate, isLoading: isMutating } = useBorrowerDataMutation(
    props.borrowerId,
  );

  const loanSummary = useGetLoanSummary().data;
  const productType = loanSummary?.loanInfo?.loanProductType;
  const refiPurpose = loanSummary?.propertyData?.refiPurpose;

  const { form, handleSubmit } = useQuestionBuilder({
    formData: data?.formData,
    screenData: {
      isWriteAccessDisabled: isWriteAccessDisabled,
      isVA: data?.isVA ?? false,
      productType,
      refiPurpose,
    },
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        if (fieldApi.name === 'dateOfBirth') {
          dateOfBirthListener(fieldApi, formApi);
        }

        if (
          fieldApi.name === 'socialSecurityNumber' ||
          fieldApi.name === 'lastName'
        ) {
          isNameOrSsnChangedListener(formApi, data);
        }

        if (fieldApi.name === 'joinedBorrower.borrowerRecordId') {
          isJoinedBorrowerChangedListener(fieldApi, formApi, data);
        }
      },
    },
    hooks: {
      onSubmit: (values) => {
        mutate(values);
      },
    },
  });

  const dropdownOptions = data?.dropdownOptions ?? EMPTY_DROPDOWN_OPTIONS;

  return (
    <form.Provider>
      <Loan1003FormContainer
        isLoading={isLoading || isMutating}
        readOnlyMode={isWriteAccessDisabled}
        onCloseClicked={() => {
          useBorrower1003Store.setState({ selectedBorrowerId: null });
        }}
        onSaveClicked={() => {
          handleSubmit();
        }}
      >
        <Loan1003FormSection title="Borrower Information">
          <BorrowerInformationGrid
            form={form}
            dropdownOptions={dropdownOptions}
          />
        </Loan1003FormSection>
        <Box sx={{ display: 'flex', gap: '1rem', width: '100%' }}>
          <Box sx={{ flex: '30' }}>
            <Loan1003FormSection title="Power Of Attorney">
              <PowerOfAttorney form={form} dropdownOptions={dropdownOptions} />
            </Loan1003FormSection>
          </Box>
          <Box sx={{ flex: '70' }}>
            <Loan1003FormSection title="Also Known As">
              <AlsoKnownAsFieldArray form={form} />
            </Loan1003FormSection>
          </Box>
        </Box>
      </Loan1003FormContainer>
    </form.Provider>
  );
}
